<!--
  AccountSessions.svelte — Active Sessions management page.

  Shows the user's active login sessions with device, location, and
  last-active timestamps.  Users can revoke individual sessions
  or revoke all sessions except the current one.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Button, Spinner, Badge } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import RelativeTime from '../../components/RelativeTime.svelte';
	import { ConfirmButton } from '../../components';
	import { listSessions, revokeSession, revokeOtherSessions } from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import type { Session } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	/** All loaded sessions. */
	let sessions: Session[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');
	/** Whether a revoke-all operation is in progress. */
	let revokingAll = $state(false);
	/** Set of session IDs currently being revoked individually. */
	let revokingIds: Set<number> = $state(new Set());

	/** Fetch sessions from the API. */
	async function loadSessions(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch active sessions from the API. */
		try {
			sessions = await listSessions();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load sessions');
		} finally {
			loading = false;
		}
	}

	onMount(loadSessions);

	/** Revoke a single session. */
	async function handleRevoke(id: number): Promise<void> {
		revokingIds.add(id);
		revokingIds = revokingIds;
		/** Call the revoke API and remove the session from the list on success. */
		try {
			await revokeSession(id);
			sessions = sessions.filter((s) => s.id !== id);
			toastSuccess(t2('Session revoked.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to revoke session'));
		} finally {
			revokingIds.delete(id);
			revokingIds = revokingIds;
		}
	}

	/** Revoke all sessions except the current one. */
	async function handleRevokeAll(): Promise<void> {
		revokingAll = true;
		/** Revoke all non-current sessions and keep only the current one. */
		try {
			const result = await revokeOtherSessions();
			sessions = sessions.filter((s) => s.isCurrent);
			toastSuccess(t2('Revoked {{count}} session(s).', { count: result.revoked }));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to revoke sessions'));
		} finally {
			revokingAll = false;
		}
	}

	/**
	 * Parse the user-agent string into a short human-readable device label.
	 * Handles common browser/OS patterns without requiring an external library.
	 */
	function parseDevice(ua: string | null): string {
		if (!ua) return t2('Unknown device');

		let browser = 'Unknown browser';
		let os = 'Unknown OS';

		/* Browser detection (order matters — Edge/Opera first). */
		if (ua.includes('Edg/')) browser = 'Edge';
		else if (ua.includes('OPR/')) browser = 'Opera';
		else if (ua.includes('Chrome/')) browser = 'Chrome';
		else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
		else if (ua.includes('Firefox/')) browser = 'Firefox';

		/* OS detection. */
		if (ua.includes('Windows')) os = 'Windows';
		else if (ua.includes('Macintosh')) os = 'macOS';
		else if (ua.includes('Linux')) os = 'Linux';
		else if (ua.includes('Android')) os = 'Android';
		else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

		return `${browser} on ${os}`;
	}
</script>

<div class="space-y-6">
	<div class="flex items-center justify-between">
		<div>
			<h3 class="text-xl font-semibold text-gray-900 dark:text-white">{$t("Active Sessions")}</h3>
			<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
				{$t("Devices and browsers where you're currently signed in.")}
			</p>
		</div>

		{#if sessions.length > 1}
			<ConfirmButton size="sm" color="red" disabled={revokingAll} onconfirm={handleRevokeAll}>
				{#if revokingAll}
					<Spinner size="4" class="mr-2" />
				{/if}
				{$t("Revoke all others")}
			</ConfirmButton>
		{/if}
	</div>

	{#if loading}
		<div class="flex justify-center py-12">
			<Spinner size="8" />
		</div>
	{:else if error}
		<AbstractCard class="max-w-none">
			<p class="text-red-500">{error}</p>
		</AbstractCard>
	{:else if sessions.length === 0}
		<AbstractCard class="max-w-none">
			<p class="py-4 text-center text-gray-500 dark:text-gray-400">{$t("No active sessions.")}</p>
		</AbstractCard>
	{:else}
		<div class="space-y-3">
			{#each sessions as session (session.id)}
				<AbstractCard class="max-w-none">
					<div class="flex items-center justify-between gap-4">
						<div class="flex min-w-0 items-center gap-4">
							<!-- Device icon -->
							<div
								class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-depth"
							>
								{#if session.userAgent?.includes('Mobile') || session.userAgent?.includes('Android') || session.userAgent?.includes('iPhone')}
									<i class="fa-solid fa-mobile-screen text-gray-500 dark:text-gray-400"></i>
								{:else}
									<i class="fa-solid fa-desktop text-gray-500 dark:text-gray-400"></i>
								{/if}
							</div>

							<div class="min-w-0">
								<div class="flex flex-wrap items-center gap-2">
									<span class="text-sm font-medium text-gray-900 dark:text-white">
										{parseDevice(session.userAgent)}
									</span>
									{#if session.isCurrent}
										<Badge color="green" class="text-xs">{$t("Current")}</Badge>
									{/if}
								</div>
								<div class="mt-0.5 space-x-3 text-xs text-gray-500 dark:text-gray-400">
									{#if session.location}
										<span>
											<i class="fa-solid fa-location-dot mr-1"></i>{session.location}
										</span>
									{/if}
									<span>
										<i class="fa-solid fa-clock mr-1"></i>{$t("Active")} <RelativeTime
											date={session.lastActiveAt}
										/>
									</span>
								</div>
							</div>
						</div>

						{#if !session.isCurrent}
							<div class="flex-shrink-0">
								<Button
									size="xs"
									color="red"
									outline
									disabled={revokingIds.has(session.id)}
									onclick={() => handleRevoke(session.id)}
								>
									{#if revokingIds.has(session.id)}
										<Spinner size="4" class="mr-1" />
									{/if}
									{$t("Revoke")}
								</Button>
							</div>
						{/if}
					</div>
				</AbstractCard>
			{/each}
		</div>
	{/if}
</div>
